import { useEffect, useMemo, useRef } from 'react';

import debounce from 'lodash/debounce';

import { useGamesSeen } from '../../components/GamesList/hooks/useGamesSeen';
import { DEBOUNCE_DELAY, isElementSeen } from '../../components/GamesList/seenGamesImpression';
import { useStores } from '../../stores/useStores';

import { CategoryBannerCard } from './CategoryBannerCard';
import { CategoryBannerErrorBoundary } from './CategoryBannerErrorBoundary';
import { getCategoryBannerAnalyticsProps, sendCategoryBannerEvent } from './getCategoryBannerAnalyticsProps';
import type { CategoryBanner } from './types';
import { useCategoryBannerCta } from './useCategoryBannerCta';

function CategoryBannerRowContent({ banner }: { banner: CategoryBanner }) {
  const { eventTriggerStore } = useStores();
  const { handleCtaClick } = useCategoryBannerCta();
  const rowRef = useRef<HTMLDivElement>(null);
  // Read at send time, so a refetch (same id, new CTA) between scroll stops reports current metadata.
  const latestBannerRef = useRef(banner);
  latestBannerRef.current = banner;

  // Same cadence as the lobby games impression: once scrolling settles, if at least half the row is on screen.
  const seenListener = useMemo(
    () =>
      debounce(() => {
        const row = rowRef.current;
        if (!row || !isElementSeen(row)) return;
        void sendCategoryBannerEvent('lobby_banner', {
          ...getCategoryBannerAnalyticsProps(latestBannerRef.current, eventTriggerStore.lastEventTriggerResponse),
          event_action: 'impression',
        });
      }, DEBOUNCE_DELAY),
    [eventTriggerStore],
  );
  useEffect(() => () => seenListener.cancel(), [seenListener]);
  useGamesSeen(seenListener);

  return (
    <div ref={rowRef} className="games" data-category-banner-id={banner.id}>
      <CategoryBannerCard banner={banner} onCtaClick={() => handleCtaClick(banner)} />
    </div>
  );
}

export function CategoryBannerRow({ banner }: { banner: CategoryBanner }) {
  return (
    <CategoryBannerErrorBoundary>
      <CategoryBannerRowContent banner={banner} />
    </CategoryBannerErrorBoundary>
  );
}
